import React, { useEffect, useMemo, useState } from 'react';
import { Popup } from 'devextreme-react/popup';
import Form, { FormRef, GroupItem, Label, SimpleItem } from "devextreme-react/form";
import { Button } from 'devextreme-react/button';
import ScrollView from 'devextreme-react/scroll-view';
import IPlanEnteteParametreRequis, { PlanEnteteParametre } from '../../models/IPlanEnteteParametreRequis';
import Guid from '../../models/Guid';

interface IColumnOption {
  key: string;
  label: string;
  parametre: PlanEnteteParametre;
  checked: boolean;
}

interface IPopupSelectionColonnesProps {
  visible: boolean;
  onClose: () => void;
  onApply: (parametres: IPlanEnteteParametreRequis[]) => void;
  parametres?: IPlanEnteteParametreRequis[];
  idPlanEntete?: Guid;
}

const COLUMN_OPTIONS: IColumnOption[] = [
  { key: 'categorieClient', label: "Catégorie de client", parametre: PlanEnteteParametre.CategorieClient, checked: false },
  { key: 'datesDebutFin', label: "Dates de début et de fin", parametre: PlanEnteteParametre.DatesDebutFin, checked: true },
  { key: 'descriptionsWeb', label: "Descriptions française et anglaise (utilisé sur le web)", parametre: PlanEnteteParametre.DescriptionFr, checked: false },
  { key: 'duree', label: "Durée", parametre: PlanEnteteParametre.Duree, checked: true },
  { key: 'joursSemaine', label: "Jours de semaine", parametre: PlanEnteteParametre.JoursSemaine, checked: true },
  { key: 'montantHebergementTaxable', label: "Montant d'hébergement taxable", parametre: PlanEnteteParametre.MontantHebergementTaxable, checked: false },
  { key: 'nombreAdultes', label: "Nombre d'adultes", parametre: PlanEnteteParametre.NbAdultes, checked: true },
  { key: 'nombreEnfants', label: "Nombre d'enfants", parametre: PlanEnteteParametre.NbEnfants, checked: true },
  { key: 'permettreEnfantsAdulte', label: "Permettre autant d'enfants que d'adultes avant de charger des enfants additionnels", parametre: PlanEnteteParametre.AutantEnfantsQueAdultes, checked: false },
  { key: 'statutMembre', label: "Statut Membre", parametre: PlanEnteteParametre.StatutMembre, checked: true },
  { key: 'statutResident', label: "Statut Résident", parametre: PlanEnteteParametre.StatutResident, checked: false },
  { key: 'typeEquipement', label: "Type d'Équipement", parametre: PlanEnteteParametre.TypeEquipement, checked: false },
  { key: 'typeRapport', label: "Type (requis pour grouper dans le rapport d'hébergement annuel)", parametre: PlanEnteteParametre.Type, checked: false },
  { key: 'unite', label: "Unité", parametre: PlanEnteteParametre.Unite, checked: false },
  { key: 'tranchesAge', label: "Tranches d'âge", parametre: PlanEnteteParametre.TranchesAge, checked: false },
];

const PopupSelectionColonnes = ({ visible, onClose, onApply, parametres, idPlanEntete }: IPopupSelectionColonnesProps) => {
  // Compute initial form data based on parametres or defaults
  const getInitialFormData = useMemo(() => {
    const data: Record<string, boolean> = {};
    COLUMN_OPTIONS.forEach(option => {
      const parametre = parametres?.find(p => p.parametre === option.parametre);
      data[option.key] = parametre?.requis ?? option.checked;
    });
    return data;
  }, [parametres]);

  const [columnStates, setColumnStates] = useState<Record<string, boolean>>(getInitialFormData);

  // Reset state when popup becomes visible
  useEffect(() => {
    if (visible) {
      setColumnStates(getInitialFormData);
    }
  }, [visible, getInitialFormData]);

  const formData = useMemo(() => {
    const data: Record<string, boolean> = {};
    COLUMN_OPTIONS.forEach(option => {
      data[option.key] = columnStates[option.key];
    });
    return data;
  }, [columnStates]);

  const handleValueChanged = (key: string, value: boolean) => {
    setColumnStates(prev => ({
      ...prev,
      [key]: value
    }));
  };

  const handleApply = () => {
    const updatedParametres: IPlanEnteteParametreRequis[] = [];
    
    COLUMN_OPTIONS.forEach(option => {
      const isChecked = columnStates[option.key];
      const existingParametre = parametres?.find(p => p.parametre === option.parametre);
      
      if (existingParametre) {
        updatedParametres.push({
          ...existingParametre,
          requis: isChecked
        });
      } else {
        updatedParametres.push({
          idPlanEnteteParametreRequis: Guid.empty,
          idPlanEntete: idPlanEntete || Guid.empty,
          requis: isChecked,
          parametre: option.parametre,
          isDeleted: false,
          dateCreation: new Date(),
          dateModification: new Date(),
          idUsagerCreation: Guid.empty,
          idUsagerModification: Guid.empty
        });
      }
    });

    onApply(updatedParametres);
    onClose();
  };

  return (
    <Popup
      visible={visible}
      onHiding={onClose}
      dragEnabled={true}
      hideOnOutsideClick={false}
      showCloseButton={true}
      showTitle={true}
      title="Choix de colonnes"
      container=".dx-viewport"
      width={500}
      height={600}
    >
      <div style={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
        <div style={{ marginBottom: '1rem' }}>
          <p>Veuillez cocher les colonnes dont vous avez besoin.</p>
        </div>
        <ScrollView height="100%" style={{ flex: 1 }}>
          <Form
            formData={formData}
            labelLocation="left"
            showColonAfterLabel={false}
            readOnly={false}
          >
            <GroupItem>
              {COLUMN_OPTIONS.map((option) => (
                <SimpleItem
                  key={option.key}
                  dataField={option.key}
                  editorType="dxCheckBox"
                  editorOptions={{
                    onValueChanged: (e: any) => handleValueChanged(option.key, e.value)
                  }}
                >
                  <Label location="right" showColon={false} text={option.label} />
                </SimpleItem>
              ))}
            </GroupItem>
          </Form>
        </ScrollView>
        <div style={{ marginTop: '1rem', paddingTop: '1rem', borderTop: '1px solid #ddd' }}>
          <p style={{ fontSize: '0.9em', color: '#666', marginBottom: '1rem' }}>
            Veuillez noter que si vous décidez de masquer une colonne dans un plan existant, les valeurs entrées précédemment demeurent et restent actives. Pour les voir et les modifier, cliquez sur "..." à la fin de la ligne.
          </p>
          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '0.5rem' }}>
            <Button
              text="Appliquer"
              type="default"
              onClick={handleApply}
            />
            <Button
              text="Annuler"
              type="normal"
              onClick={() => {
                setColumnStates(getInitialFormData);
                onClose();
              }}
            />
          </div>
        </div>
      </div>
    </Popup>
  );
};

export default PopupSelectionColonnes;

